import { Typography } from "@material-ui/core";
import React from "react";
import { useSelector } from "react-redux";
import { IApplicationState } from "redux/root-reducer";
import styles from "./Details.module.scss";
import { t } from "i18next";
import { getMaxTemp, getMinTemp } from "nodeDetails/functions/dataFunctions";
import { createSelector } from "reselect";
import { UtlType } from "nodeDetails/types";

interface KeyValuesProps {
  utlType?: string;
  isIndoorClimate?: boolean;
}

export const KeyValues = ({ utlType, isIndoorClimate }: KeyValuesProps) => {
  const {
    data,
    totalConsumption,
    averageTemperature,
    emissions,
    cost,
    averageConsumption,
    averageRelativeHumidity,
  } =
    useSelector(
      (store: IApplicationState) =>
        store.node.selectedInstallation?.detailedInstallation
    ) || {};

  const getDetailsState = createSelector(
    (state: IApplicationState) => state,
    (globalState) => ({
      currentTemperature: globalState.node.currentTemperatureForNode,
      currency: globalState.common.currentUserInfo.DomainSettings.Currency,
      max: getMaxTemp(
        globalState.node.selectedInstallation?.detailedInstallation.data || []
      ),
      min: getMinTemp(
        globalState.node.selectedInstallation?.detailedInstallation.data || []
      ),
      installationError: globalState.node.error,
    })
  );
  const { max, min, currency, currentTemperature } =
    useSelector(getDetailsState);

  let keyValuesJSX = <></>;
  if (
    utlType === UtlType.HeatCostAllocator ||
    utlType === UtlType.HeatCostAllocatorE
  ) {
    keyValuesJSX = (
      <>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Total consumption")}</Typography>
          <Typography variant="h5">
            {data ? `${totalConsumption ?? 0}` : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Cost")}</Typography>
          <Typography variant="h5">
            {cost ? `${cost} ${currency}` : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Daily average")}</Typography>
          <Typography variant="h5">
            {data ? `${averageConsumption ?? 0}` : "-"}
          </Typography>
        </div>
      </>
    );
  } else if (isIndoorClimate) {
    keyValuesJSX = (
      <>
        <div className={styles.row}>
          <Typography variant="h4">{t("Current temperature")}</Typography>
          <Typography variant="h5">
            {currentTemperature ? currentTemperature + " °C" : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="h4">{t("Min temperature")}</Typography>
          <Typography variant="h5">
            {min && !min.includes("Infinity") ? min + " °C" : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="h4">{t("Max temperature")}</Typography>
          <Typography variant="h5">
            {max && !max.includes("Infinity") ? max + " °C" : "-"}{" "}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="h4">{t("Average temperature")}</Typography>
          <Typography variant="h5">
            {averageTemperature ? (averageTemperature * 10) / 10 + " °C" : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="h4">{t("Average relative humidity")}</Typography>
          <Typography variant="h5">
            {averageRelativeHumidity
              ? (averageRelativeHumidity * 10) / 10 + " %"
              : "-"}
          </Typography>
        </div>
      </>
    );
  } else if (!isIndoorClimate) {
    keyValuesJSX = (
      <>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Total consumption")}</Typography>
          <Typography variant="h5">
            {totalConsumption && data
              ? `${totalConsumption} ${data[0].Unit}`
              : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Cost")}</Typography>
          <Typography variant="h5">
            {cost ? `${cost} ${currency}` : "-"}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Daily average")}</Typography>
          <Typography variant="h5">
            {averageConsumption ? averageConsumption : "-"}{" "}
            {averageConsumption && data ? data[0].Unit : ""}
          </Typography>
        </div>
        <div className={styles.row}>
          <Typography variant="subtitle2">{t("Carbon footprint")}</Typography>
          <Typography variant="h5">
            {emissions ? emissions + " kg" : "-"}
          </Typography>
        </div>
      </>
    );
  }

  return keyValuesJSX;
};
